import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, FileText, MessageSquare, Globe, Zap, BookOpen, Lock, Sparkles } from 'lucide-react';

const FeatureCarousel = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Mic,
      title: t('features.transcribe.title'),
      description: t('features.transcribe.description', { defaultValue: 'Industry-leading accuracy on noisy, multi-speaker audio.' }),
    },
    {
      icon: FileText,
      title: t('features.formattedNotes.title'),
      description: t('features.formattedNotes.description', { defaultValue: 'Auto-generated bullets, action items, or prose.' }),
    },
    {
      icon: Globe,
      title: t('features.languages', { defaultValue: '30 Languages' }),
      description: t('features.languages.description', { defaultValue: 'Including live translate across any pair.' }),
    },
    {
      icon: Zap,
      title: t('features.realtime', { defaultValue: 'Real-time' }),
      description: t('features.realtime.description', { defaultValue: "Transcript appears while you're still talking." }),
    },
    {
      icon: MessageSquare,
      title: t('features.chat.title'),
      description: t('features.chat.description', { defaultValue: 'Extract key insights, list action items, ask questions.' }),
    },
    {
      icon: Lock,
      title: t('features.secureStorage', { defaultValue: 'Secure Storage' }),
      description: t('features.secureStorage.description', { defaultValue: 'On-device by default, E2EE sync when you want it.' }),
    },
  ];

  return (
    <div style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)', position: 'relative', zIndex: 2 }} className="py-24 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'var(--v2-primary-light)',
              color: 'var(--v2-primary)',
              padding: '6px 16px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 600,
              marginBottom: '16px',
            }}
          >
            <Sparkles className="w-4 h-4" />
            {t('features.badge', { defaultValue: 'AI-Powered' })}
          </div>
          <h2
            style={{
              fontSize: 'clamp(26px, 3.5vw, 38px)',
              fontWeight: 800,
              color: 'var(--v2-ink)',
              marginBottom: '12px',
            }}
          >
            {t('features.titleV2', 'Everything a voice memo should have been')}
          </h2>
          <p style={{ fontSize: '16px', color: 'var(--v2-ink-muted)', maxWidth: '540px', margin: '0 auto' }}>
            {t('features.subtitle')}
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '20px',
          }}
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                style={{
                  background: 'var(--v2-card)',
                  border: '1px solid var(--v2-line)',
                  borderRadius: '18px',
                  padding: '24px',
                  transition: 'transform 0.18s, box-shadow 0.18s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 32px rgba(91,74,231,0.10)';
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'transparent';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'none';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--v2-line)';
                }}
              >
                <div
                  style={{
                    background: 'var(--v2-primary-light)',
                    color: 'var(--v2-primary)',
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <h3
                  style={{
                    fontSize: '15px',
                    fontWeight: 700,
                    color: 'var(--v2-ink)',
                    marginBottom: '8px',
                  }}
                >
                  {feature.title}
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--v2-ink-muted)', lineHeight: 1.6 }}>
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FeatureCarousel;
